/**
 * Mapper puro DTO real (`buscarCadastroCliente`) → shape de UI (AW2-1136
 * Etapa 1) — porta de `IdentificacaoCliente.jsx` real sem `FieldModel`,
 * mesmo padrão já usado para Devedor (`devedorController.js`). Diferente de
 * Devedor, a aba Cliente inteira é somente-leitura em produção (confirmado
 * por leitura direta — todo campo de `IdentificacaoCliente.jsx` real é
 * `disabled` incondicional) — não há campos "editáveis quando isEditMode"
 * aqui, então não existe a separação entre campos livres e travados que
 * `devedorController.js` precisa fazer.
 *
 * `usuarioPrincipal`/`usuarioTelemarketing` são campos PLANOS do mesmo DTO
 * (`cliente?.usuarioPrincipal`/`cliente?.usuarioTelemarketing`) — confirmado
 * por leitura direta (únicas referências a esses nomes no repositório estão
 * dentro do próprio `IdentificacaoCliente.jsx` real, sem nenhum dispatch
 * Redux alimentando-os); a suposição inicial de que viriam de
 * `findUsuariosComercialPrincipalClienteAction`/`findUsuariosTelemarketingAction`
 * (dispatches usados para outro propósito, fora do escopo desta aba) foi
 * descartada antes de escrever este mapper.
 */
const CAMPOS_IDENTIFICACAO_CLIENTE = [
  'idCliente',
  'nomeCliente',
  'origemLead',
  'statusCliente',
  'nomeFantasia',
  'inscricaoEstadual',
  'inscricaoMunicipal',
  'categoria',
  'cpfCnpj',
  'preposto',
  'identificacaoInterna',
  'filialResponsavel',
  'usuarioPrincipal',
  'usuarioTelemarketing',
];

/**
 * `ramosAtividade` real é um array de objetos (`{ nomeRamoAtividade, ... }`)
 * — `IdentificacaoCliente.jsx` real junta em uma única string exibida
 * (`.map(item => item.nomeRamoAtividade).join('; ')`), mesmo critério
 * replicado aqui.
 */
function mapearRamosAtividade(ramosAtividade) {
  return (ramosAtividade || []).map(item => item.nomeRamoAtividade).join('; ');
}

/**
 * `observacao` real é HTML rico (produção usa um editor de texto formatado,
 * `<Editor readOnly>`) — o protótipo não tem um viewer de rich-text, só o
 * `Field` genérico (texto plano). Achado ao vivo: exibir o HTML cru mostra
 * as tags literalmente (`<p>...</p>`) para o usuário. Removendo as tags via
 * regex (não `dangerouslySetInnerHTML` — evita processar HTML vindo do
 * backend sem sanitização) para uma prévia em texto plano, aceitável para
 * uma tela somente-leitura.
 */
function removerTagsHtml(html) {
  return (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const CAMPOS_ENDERECO = ['cep', 'endereco', 'numero', 'complemento', 'bairro', 'cidade', 'estado'];

/**
 * Endereço do cliente (AW2-1136 Etapa 2) — ÚNICO objeto (`cliente?.endereco`),
 * não uma lista — confirmado por leitura direta de `EnderecoCliente.jsx`
 * real, diferente de Endereços do Devedor (múltiplos, com "Principal").
 */
function mapearEndereco(endereco) {
  return CAMPOS_ENDERECO.reduce(
    (campos, campo) => ({ ...campos, [campo]: endereco?.[campo] ?? '' }),
    {},
  );
}

const CAMPOS_TELEFONE = ['idTelefone', 'tipoTelefone', 'ddd', 'numero', 'observacao', 'principal', 'permitirWhatsApp'];

/**
 * Telefones do cliente (AW2-1136 Etapa 2) — lista somente-leitura, SEM
 * ordenação por `principal` — confirmado por leitura direta de
 * `TelefoneDadosCliente.jsx` real (`telefones.map(...)` direto, sem
 * `.sort()`), diferente de `mapearTelefones` de Devedor (que ordena
 * principal-primeiro). Replicar a ordem exata em que o backend retorna.
 */
function mapearTelefones(telefones) {
  return (telefones || []).map(telefone => CAMPOS_TELEFONE.reduce(
    (campos, campo) => ({ ...campos, [campo]: telefone?.[campo] ?? '' }),
    { principal: telefone?.principal ?? false, permitirWhatsApp: telefone?.permitirWhatsApp ?? false },
  ));
}

const CAMPOS_EMAIL = ['email', 'permitirPadrao', 'permitirBoleto', 'permitirNotaFiscal'];

/**
 * E-mails do cliente (AW2-1136 Etapa 3) — lista somente-leitura, 3
 * checkboxes por item (Padrão/Boleto/Nota Fiscal), TODOS independentes —
 * confirmado por leitura direta de `EmailProvisorio.jsx` real
 * (`components/UI/EmailsProvisorio/Email/EmailProvisorio.jsx`): não existe
 * nenhuma lógica de exclusividade mútua entre e-mails para nenhum dos 3
 * campos. Diferente de `mapearEmails` de Devedor (Etapa 5 da AW2-1134,
 * `principal` único por construção via `useListaComPrincipal`), aqui
 * "Padrão" pode legitimamente estar `true` em mais de um item ao mesmo
 * tempo (AC6 da AW2-1136) — não usar `useListaComPrincipal` para esta
 * lista.
 */
function mapearEmails(emails) {
  return (emails || []).map(email => CAMPOS_EMAIL.reduce(
    (campos, campo) => ({ ...campos, [campo]: email?.[campo] ?? '' }),
    {
      permitirPadrao: email?.permitirPadrao ?? false,
      permitirBoleto: email?.permitirBoleto ?? false,
      permitirNotaFiscal: email?.permitirNotaFiscal ?? false,
    },
  ));
}

/**
 * Dados do Advogado do cliente (AW2-1136 Etapa 3) — porta de
 * `DadosAdvogadoCliente.jsx` real. Diferente do Advogado de Devedor (2
 * strings planas, `nomeAdvogado`/`telefoneAdvogado`), aqui o telefone é um
 * SUB-OBJETO (`cliente?.telefoneAdvogado`, com Tipo Telefone/DDD/Número/
 * Observação/WhatsApp) — confirmado por leitura direta, mais rico que o
 * equivalente de Devedor.
 */
const CAMPOS_TELEFONE_ADVOGADO = ['tipoTelefone', 'ddd', 'numero', 'observacao', 'permitirWhatsApp'];

function mapearAdvogado(dadosCliente) {
  return {
    nomeAdvogado: dadosCliente?.nomeAdvogado ?? '',
    telefoneAdvogado: CAMPOS_TELEFONE_ADVOGADO.reduce(
      (campos, campo) => ({ ...campos, [campo]: dadosCliente?.telefoneAdvogado?.[campo] ?? '' }),
      { permitirWhatsApp: dadosCliente?.telefoneAdvogado?.permitirWhatsApp ?? false },
    ),
  };
}

export function mapearClienteParaFormFields(dadosCliente) {
  return {
    ...CAMPOS_IDENTIFICACAO_CLIENTE.reduce(
      (campos, campo) => ({ ...campos, [campo]: dadosCliente?.[campo] ?? '' }),
      {},
    ),
    ...mapearAdvogado(dadosCliente),
    ramosAtividade: mapearRamosAtividade(dadosCliente?.ramosAtividade),
    observacao: removerTagsHtml(dadosCliente?.observacao),
    endereco: mapearEndereco(dadosCliente?.endereco),
    telefones: mapearTelefones(dadosCliente?.telefones),
    emails: mapearEmails(dadosCliente?.emails),
  };
}
